import { useState } from 'react';
import { VITALS_ATTENTION, vitalsWorthMentioning } from '@rx/shared';

/**
 * Vitals — optional, opt-in, and off by default.
 *
 * Denise's own chart carries daily weight, blood pressure, pulse and temperature, with
 * the caveat "don't think Richard is at this point." So this exists but is never in the
 * way; it is enabled only if and when he is ready for it.
 *
 * Steppers rather than a keyboard: entering three digits with a tremor is a worse
 * experience than tapping a large button a few times, and a mistyped 55 for 155 is a
 * clinically meaningful error.
 */

export interface VitalsSubmission {
  pulseBpm?: number;
  systolic?: number;
  diastolic?: number;
  weightLb?: number;
  temperatureF?: number;
}

interface FieldSpec {
  key: keyof VitalsSubmission;
  label: string;
  unit: string;
  step: number;
  start: number;
  min: number;
  max: number;
}

const FIELDS: FieldSpec[] = [
  { key: 'pulseBpm', label: 'Pulse', unit: 'bpm', step: 1, start: 70, min: 20, max: 250 },
  { key: 'systolic', label: 'Top number', unit: 'mmHg', step: 1, start: 120, min: 50, max: 260 },
  { key: 'diastolic', label: 'Bottom number', unit: 'mmHg', step: 1, start: 78, min: 20, max: 200 },
  { key: 'weightLb', label: 'Weight', unit: 'lb', step: 1, start: 170, min: 20, max: 1000 },
  { key: 'temperatureF', label: 'Temperature', unit: '°F', step: 1, start: 98, min: 80, max: 115 },
];

export function VitalsFlow({
  onSubmit,
  onCancel,
}: {
  onSubmit: (v: VitalsSubmission) => Promise<void>;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<VitalsSubmission>({});
  const [busy, setBusy] = useState(false);

  const flags = vitalsWorthMentioning(values);
  const anything = Object.values(values).some((v) => v !== undefined);

  function bump(spec: FieldSpec, delta: number): void {
    setValues((prev) => {
      const current = prev[spec.key] ?? spec.start;
      const next = Math.min(spec.max, Math.max(spec.min, current + delta * spec.step));
      return { ...prev, [spec.key]: next };
    });
  }

  async function save(): Promise<void> {
    if (!anything) return;
    setBusy(true);
    try {
      await onSubmit(values);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app">
      <h1>Readings</h1>
      <p className="muted">Only fill in what you have. Anything you skip is left out.</p>

      {FIELDS.map((spec) => {
        const value = values[spec.key];
        return (
          <section className="card" key={spec.key}>
            <div className="card-head">
              <span className="med">{spec.label}</span>
              <span className="when">
                {value === undefined ? '—' : `${String(value)} ${spec.unit}`}
              </span>
            </div>
            <div className="stepper">
              <button
                type="button"
                className="btn-quiet step"
                aria-label={`Lower ${spec.label}`}
                onClick={() => {
                  bump(spec, -1);
                }}
              >
                −
              </button>
              <button
                type="button"
                className="btn-quiet step"
                aria-label={`Raise ${spec.label}`}
                onClick={() => {
                  bump(spec, 1);
                }}
              >
                +
              </button>
              {value !== undefined && (
                <button
                  type="button"
                  className="btn-quiet step clear"
                  aria-label={`Clear ${spec.label}`}
                  onClick={() => {
                    setValues((prev) =>
                      // Rebuild without the key rather than deleting it: an undefined
                      // value would still be sent and stored as an empty reading.
                      Object.fromEntries(
                        Object.entries(prev).filter(([k]) => k !== spec.key),
                      ),
                    );
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </section>
        );
      })}

      {/*
        The precautions document notes that a very slow pulse or unusually low blood
        pressure can accompany excessive propranolol effect. This says the reading is
        worth mentioning — it never diagnoses, and it never blocks saving.
      */}
      {flags.length > 0 && (
        <p className="note">
          Worth telling the doctor about this reading
          {flags.includes('PULSE_LOW') && ` (pulse under ${String(VITALS_ATTENTION.pulseBpmLow)})`}
          {flags.includes('SYSTOLIC_LOW') && ' (blood pressure on the low side)'}.
        </p>
      )}

      <div className="btn-row">
        <button className="btn-primary" disabled={busy || !anything} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </main>
  );
}
